/**
 * Localidade exata vs. zona próxima (módulo puro, sem rede e sem custo).
 *
 * Problema real: Perafita está FORA de Leça da Palmeira mas DENTRO de
 * Matosinhos. Descrever o "Kart Center de Matosinhos" como "fora de
 * Matosinhos" é falso. Aqui distinguimos três casos:
 *
 *  - `exact`   → está mesmo na localidade pedida (Leça da Palmeira);
 *  - `nearby`  → está na zona próxima (Perafita, mesmo município/limite);
 *  - `outside` → está fora da zona pedida e da zona alargada.
 *
 * O alargamento nunca é um simples booleano: é uma ÁREA concreta, nomeada e
 * com raio limitado, para nunca voltar ao raio largo antigo nem alargar em
 * silêncio para o município/cidade inteiros.
 */

export type LocalityMatch = "exact" | "nearby" | "outside";

export type ExpandedArea = {
  /** Texto visível ao utilizador: "zona próxima de Leça da Palmeira (Perafita)". */
  label: string;
  /** Localidades explicitamente incluídas no alargamento. */
  localities: string[];
  center?: { lat: number; lng: number };
  /** Limite duro do alargamento, em metros. */
  radiusMeters: number;
};

/** Teto do alargamento: poucos quilómetros, nunca a cidade inteira. */
export const MAX_EXPANDED_RADIUS_METERS = 8000;
export const MIN_EXPANDED_RADIUS_METERS = 3000;

export const normalizeLocality = (value: string | null | undefined): string =>
  (value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

export function haversineMeters(
  a: { lat: number; lng: number },
  b: { lat: number; lng: number },
): number {
  const R = 6371000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const x =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(x));
}

const mentions = (haystack: string, needle: string): boolean => {
  const n = normalizeLocality(needle);
  if (n.length < 3) return false;
  return normalizeLocality(haystack).includes(n);
};

export type LocalityClassificationInput = {
  /** Morada formatada devolvida pelo Google Places. */
  address?: string | null;
  /** Localidade extraída dos `addressComponents` (quando disponível). */
  locality?: string | null;
  /** Localidade(s) exatamente pedida(s) pelo utilizador. */
  requestedLocalities: string[];
  /** Município/região que contêm a localidade pedida (Matosinhos). */
  nearbyLocalities?: string[];
  distanceMeters?: number | null;
  expandedArea?: ExpandedArea | null;
};

/**
 * Classifica um local face à localidade pedida. Um local no mesmo município
 * NUNCA é `outside` só por não estar na localidade exata: é `nearby`.
 */
export function classifyLocality(input: LocalityClassificationInput): LocalityMatch {
  const haystack = `${input.address ?? ""} ${input.locality ?? ""}`;
  const requested = input.requestedLocalities.filter(Boolean);
  const isExact = requested.some((name) => mentions(haystack, name));
  if (isExact) return "exact";

  const area = input.expandedArea ?? null;
  const distance = typeof input.distanceMeters === "number" ? input.distanceMeters : null;

  if (area) {
    // Com área alargada ativa, o limite é DURO: fora do raio é fora, mesmo que
    // o município coincida.
    if (distance != null && distance > area.radiusMeters) return "outside";
    if (area.localities.some((name) => mentions(haystack, name))) return "nearby";
    if (distance != null && distance <= area.radiusMeters) return "nearby";
    return "outside";
  }

  const near = (input.nearbyLocalities ?? []).filter(Boolean);
  if (near.some((name) => mentions(haystack, name))) return "nearby";
  if (distance != null && distance <= MIN_EXPANDED_RADIUS_METERS) return "nearby";
  return "outside";
}

const AREA_LABEL: Record<string, (requested: string, nearby: string | null) => string> = {
  pt: (requested, nearby) =>
    nearby ? `zona próxima de ${requested} (${nearby})` : `zona próxima de ${requested}`,
  en: (requested, nearby) =>
    nearby ? `area near ${requested} (${nearby})` : `area near ${requested}`,
  es: (requested, nearby) =>
    nearby ? `zona cercana a ${requested} (${nearby})` : `zona cercana a ${requested}`,
  fr: (requested, nearby) =>
    nearby ? `zone proche de ${requested} (${nearby})` : `zone proche de ${requested}`,
  it: (requested, nearby) =>
    nearby ? `zona vicino a ${requested} (${nearby})` : `zona vicino a ${requested}`,
  de: (requested, nearby) =>
    nearby ? `Umgebung von ${requested} (${nearby})` : `Umgebung von ${requested}`,
};

export function expandedAreaLabel(
  requested: string,
  nearby: string | null,
  language = "pt",
): string {
  const lang = (language || "pt").slice(0, 2).toLowerCase();
  const build = AREA_LABEL[lang] ?? AREA_LABEL.en!;
  return build(requested, nearby && normalizeLocality(nearby) !== normalizeLocality(requested) ? nearby : null);
}

/**
 * Constrói a área alargada a partir dos candidatos REALMENTE encontrados.
 * O raio nunca é arbitrário: cobre o candidato mais próximo com uma margem
 * pequena, entre 3 km e 8 km.
 */
export function buildExpandedArea(input: {
  requestedLocality: string;
  candidates: Array<{ locality?: string | null; distanceMeters?: number | null }>;
  center?: { lat: number; lng: number } | undefined;
  language?: string;
}): ExpandedArea | null {
  const requested = input.requestedLocality.trim();
  if (!requested) return null;
  const withDistance = input.candidates
    .filter((c) => typeof c.distanceMeters === "number")
    .sort((a, b) => (a.distanceMeters ?? 0) - (b.distanceMeters ?? 0));
  const closest = withDistance[0] ?? input.candidates[0];
  if (!closest) return null;

  const localities: string[] = [requested];
  for (const candidate of input.candidates) {
    const name = (candidate.locality ?? "").trim();
    if (!name) continue;
    if (localities.some((existing) => normalizeLocality(existing) === normalizeLocality(name))) continue;
    localities.push(name);
  }

  const furthestNeeded = withDistance.length
    ? Math.max(...withDistance.map((c) => c.distanceMeters ?? 0))
    : 0;
  const radiusMeters = Math.min(
    MAX_EXPANDED_RADIUS_METERS,
    Math.max(MIN_EXPANDED_RADIUS_METERS, Math.round(furthestNeeded + 1500)),
  );

  const nearbyName = localities.find(
    (name) => normalizeLocality(name) !== normalizeLocality(requested),
  );

  return {
    label: expandedAreaLabel(requested, nearbyName ?? null, input.language),
    localities: localities.slice(0, 6),
    ...(input.center ? { center: input.center } : {}),
    radiusMeters,
  };
}

/** "em Perafita, perto de Leça da Palmeira" */
export function nearbyLocalityText(
  placeLocality: string | null | undefined,
  requestedLocality: string,
  language = "pt",
): string {
  const lang = (language || "pt").slice(0, 2).toLowerCase();
  const where = (placeLocality ?? "").trim();
  const templates: Record<string, (w: string, r: string) => string> = {
    pt: (w, r) => (w ? `em ${w}, perto de ${r}` : `perto de ${r}`),
    en: (w, r) => (w ? `in ${w}, near ${r}` : `near ${r}`),
    es: (w, r) => (w ? `en ${w}, cerca de ${r}` : `cerca de ${r}`),
    fr: (w, r) => (w ? `à ${w}, près de ${r}` : `près de ${r}`),
    it: (w, r) => (w ? `a ${w}, vicino a ${r}` : `vicino a ${r}`),
    de: (w, r) => (w ? `in ${w}, nahe ${r}` : `nahe ${r}`),
  };
  return (templates[lang] ?? templates.en!)(where, requestedLocality);
}
